/**
 * API client for the Style Library endpoints.
 */

import { apiFetch, apiRequest, getCachedAuthToken, getConfiguredApiBase } from './client'
import { createDebug } from '../utils/debugLog'

const styleLog = createDebug('dw:style-library')

export interface StyleSummary {
  id: string
  name: string
  prompt?: string
  use_reference_in_generation?: boolean
  reference_image_count: number
  tags: string[]
  thumbnail_url: string | null
  preview_image_urls: string[]
  created_at?: string
}

export interface StyleDetail {
  id: string
  name: string
  prompt: string
  reference_images: string[]
  use_reference_in_generation: boolean
  tags: string[]
  thumbnail_url: string | null
  preview_image_urls: string[]
}

export interface StyleListResponse {
  styles: StyleSummary[]
}

export interface CreateStyleFormData {
  name: string
  description: string
  referenceImages: File[]
}

export interface UserStyleListResponse {
  styles: StyleSummary[]
  count: number
  limit: number
}

export async function listStyles(): Promise<StyleListResponse> {
  return apiRequest<StyleListResponse>('/styles')
}

export async function getStyle(id: string): Promise<StyleDetail> {
  return apiRequest<StyleDetail>(`/styles/${id}`)
}

export async function getUserStyle(id: string): Promise<StyleDetail> {
  return apiRequest<StyleDetail>(`/styles/user/${id}`)
}

/**
 * Build a full URL for a style image.
 *
 * If the URL is already absolute (starts with "http"), it is a signed Supabase
 * CDN URL — pass it through directly.  For relative paths (fallback styles or
 * legacy responses), prepend the API base and attach an auth token for user
 * style paths.
 */
export function getStyleImageUrl(urlOrPath: string): string {
  if (urlOrPath.startsWith('http')) {
    styleLog('passthrough signed URL: %s', urlOrPath.slice(0, 80))
    return urlOrPath
  }
  // fallback: relative path -> API proxy
  const baseUrl = getConfiguredApiBase()
  const url = `${baseUrl}${urlOrPath}`
  if (urlOrPath.startsWith('/styles/user/')) {
    const token = getCachedAuthToken()
    if (token) {
      styleLog('proxy user style URL: %s', urlOrPath)
      return `${url}?token=${encodeURIComponent(token)}`
    }
  }
  styleLog('proxy public style URL: %s', urlOrPath)
  return url
}

export async function listUserStyles(): Promise<UserStyleListResponse> {
  return apiRequest<UserStyleListResponse>('/styles/user')
}

export async function deleteUserStyle(styleId: string): Promise<void> {
  await apiRequest<void>(`/styles/user/${styleId}`, { method: 'DELETE' })
}

export async function createUserStyle(
  data: CreateStyleFormData,
  onProgress: (step: string, progress: number) => void
): Promise<StyleSummary> {
  const formData = new FormData()
  formData.append('name', data.name)
  formData.append('description', data.description)
  for (const file of data.referenceImages) {
    formData.append('reference_images', file)
  }

  // SSE-style streaming response — use apiFetch to get the raw Response.
  const response = await apiFetch('/styles/user', {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.detail || response.statusText)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('No response body')
  }

  const decoder = new TextDecoder()
  let buffer = ''

  return new Promise<StyleSummary>((resolve, reject) => {
    let settled = false

    const processStream = async () => {
      try {
        let currentEvent = ''
        let currentData = ''

        const processBuffer = () => {
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim()
            } else if (line.startsWith('data: ')) {
              currentData = line.slice(6)
            } else if (line.trim() === '') {
              if (currentEvent && currentData) {
                const parsed = JSON.parse(currentData)
                if (currentEvent === 'progress') {
                  onProgress(parsed.step, parsed.progress)
                } else if (currentEvent === 'complete') {
                  settled = true
                  const style: StyleSummary = parsed.style
                  resolve(style)
                } else if (currentEvent === 'error') {
                  settled = true
                  reject(new Error(parsed.message || 'Style creation failed'))
                }
              }
              currentEvent = ''
              currentData = ''
            }
          }
        }

        let done = false
        while (!done) {
          const result = await reader.read()
          done = result.done

          if (result.value) {
            buffer += decoder.decode(result.value, { stream: true })
            processBuffer()
          }

          if (done) {
            buffer += decoder.decode()
            if (buffer.trim()) {
              buffer += '\n'
              processBuffer()
            }
          }
        }

        if (!settled) {
          reject(new Error('Stream ended without completion'))
        }
      } catch (err) {
        if (!settled) {
          reject(err instanceof Error ? err : new Error(String(err)))
        }
      }
    }

    processStream()
  })
}
